import { useState } from "react";
import { type Key, Label } from "react-aria-components";
import { type SubmitHandler, type UseFormReturn, useForm } from "react-hook-form";
import { useCandidateLists, useCreateCandidateList } from "@/api/candidate-lists";
import { Tabs } from "@/components/application/tabs/tabs";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { TextArea } from "@/components/base/textarea/textarea";
import { FormField, FormItem } from "@/components/ui/form";
import { MissingCandidatesModal } from "@/pages/candidate-lists/missing-candidates";
import { error, success } from "@/providers/ToastProvider";

const tabs = [
  { id: "list", label: "Add custom list" },
  { id: "blank", label: "Create with blank list" },
];

type FormValues = { name: string; candidateIds: string; description: string };

export const CreateCandidateList = ({ onCreate }: { onCreate: (id: string) => void }) => {
  const [tab, setTab] = useState<"list" | "blank">("list");
  const { refetch } = useCandidateLists({ offset: 0, limit: 50 }, "", { column: "createdAt", direction: "asc" });
  const form = useForm<FormValues>({
    defaultValues: { candidateIds: "" },
  });

  const handleCreate = async (id: string) => {
    await refetch();
    onCreate(id);
  };

  return (
    <div className="flex flex-col items-center w-full pb-4 bg-secondary h-full pt-20">
      <Tabs
        selectedKey={tab as Key}
        onSelectionChange={(key) => setTab(key as "list" | "blank")}
        className="w-[580px] max-md:hidden"
      >
        <Tabs.List className="w-full" type="button-border" items={tabs}>
          {(tab) => <Tabs.Item {...tab} className="flex-grow" />}
        </Tabs.List>
      </Tabs>
      {tab === "list" && <UploadListForm form={form} onCreate={handleCreate} />}
      {tab === "blank" && <BlankListForm form={form} onCreate={handleCreate} />}
    </div>
  );
};

const getNumberOfIds = (ids: string): number => {
  return parseIds(ids).length;
};

const parseIds = (ids: string): string[] => {
  const byNewline = ids.replaceAll("@", "").split("\n").filter(Boolean);
  // Separated by commas
  if (byNewline.length === 1) return byNewline[0].split(",").filter(Boolean);
  return byNewline;
};

const UploadListForm = ({ onCreate, form }: { onCreate: (id: string) => void; form: UseFormReturn<FormValues> }) => {
  const { mutateAsync, isPending } = useCreateCandidateList();

  const onSubmit: SubmitHandler<{ name: string; candidateIds: string; description: string }> = async (payload) => {
    const candidateIds = parseIds(payload.candidateIds);
    try {
      const res = await mutateAsync({ ...payload, candidateIds });
      if (res.failedCandidateIds.length > 0) {
        setMissingCandidates({ listId: res.id, missingCandidates: res.failedCandidateIds });
      } else {
        success("Candidate List created successfully");
        onCreate(res.id);
      }
    } catch (err) {
      error("Failed to create Candidate List");
      throw err;
    }
  };
  const idsCount = getNumberOfIds(form.watch("candidateIds"));
  const [missingCandidates, setMissingCandidates] = useState<{ listId: string; missingCandidates: string[] }>();

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="w-full md:w-[580px]">
      {missingCandidates && (
        <MissingCandidatesModal
          candidateIds={missingCandidates.missingCandidates}
          onClose={() => onCreate(missingCandidates.listId)}
        />
      )}
      <div className="relative flex flex-col gap-4 sm:gap-5 py-4">
        <FormField
          control={form.control}
          name="candidateIds"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <TextArea
                isRequired
                label="Candidate IDs"
                maxLength={255}
                placeholder={`Paste creator usernames here e.g
@creator1
@creator2`}
                {...field}
                rows={8}
              />
              <Label className="text-sm italic">
                Creator usernames (the handles shown in the table) should each be on a new line or separated by commas.
                @ symbols are optional.
              </Label>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <Input
                autoFocus
                isRequired
                label="Name"
                maxLength={80}
                placeholder="Candidate List Name"
                {...field}
              ></Input>
              <span className="text-xs">Max 80 characters</span>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <TextArea
                maxLength={200}
                label="Description"
                placeholder="Candidate List Description"
                {...field}
              ></TextArea>
              <span className="text-xs">Max 200 characters</span>
            </FormItem>
          )}
        />
      </div>
      <div className="flex flex-row items-center justify-end gap-2">
        {idsCount > 0 && <Badge>Importing {idsCount} Candidates</Badge>}
        <Button color="primary" size="lg" type="submit" isDisabled={isPending || idsCount === 0}>
          Create
        </Button>
      </div>
    </form>
  );
};

const BlankListForm = ({ onCreate, form }: { onCreate: (id: string) => void; form: UseFormReturn<FormValues> }) => {
  const { mutateAsync, isPending } = useCreateCandidateList();

  const onSubmit: SubmitHandler<{ name: string; description: string }> = async (payload) => {
    try {
      const res = await mutateAsync({ name: payload.name, description: payload.description });
      success("Candidate List created successfully");
      onCreate(res.id);
    } catch (err) {
      error("Failed to create Candidate List");
      throw err;
    }
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="w-full md:w-[580px]">
      <div className="relative flex flex-col gap-4 sm:gap-5 py-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <Input
                autoFocus
                isRequired
                label="Name"
                maxLength={80}
                placeholder="Candidate List Name"
                {...field}
              ></Input>
              <span className="text-xs">Max 80 characters</span>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <TextArea
                maxLength={200}
                rows={2}
                label="Description"
                placeholder="Candidate List Description"
                {...field}
              ></TextArea>
              <span className="text-xs">Max 200 characters</span>
            </FormItem>
          )}
        />
      </div>
      <div className="flex flex-row items-center justify-end gap-2">
        <Button color="primary" size="lg" type="submit" isDisabled={isPending}>
          Create
        </Button>
      </div>
    </form>
  );
};
